import { useCallback, useContext, useState, useRef } from 'react'
import styled from '@emotion/styled'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'
import { useHistory, useParams } from 'react-router-dom'
import { selectIsUserAdmin } from '@authed/selectors'
import { useHasEllipsis } from '@global/hooks'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { integrationContext } from '../../contexts'
import { t } from '@lingui/core/macro'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'inline-block',
    width: 250,
    marginRight: 32,
    marginBottom: 32,
  },
  activeIcon: {
    color: theme.palette.success.light,
  },
  directionIcon: {
    color: theme.palette.info.light,
  },
  name: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  editIntegration: {
    fontSize: '.95em',
  },
  buttonWrapper: {
    whiteSpace: 'nowrap',
  },
  customWidth: {
    maxWidth: 200,
  },
}))

const SUPPORTS_RUN_NOW = ['facebook-ads', 'google-dcm']

export function IntegrationCard({ integrationAssignment }) {
  const { inboundById, outboundById } = useContext(integrationContext)
  const integration =
    outboundById[integrationAssignment.integration_id] ||
    inboundById[integrationAssignment.integration_id] ||
    {}
  const dayHasElapsed = SUPPORTS_RUN_NOW.includes(integration.slug)
    ? (new Date().getTime() - new Date(integrationAssignment.last_started_at).getTime()) / 1000 >
      86400
    : true
  const [canRunNow, setCanRunNow] = useState(
    integrationAssignment.last_completed_at >= integrationAssignment.last_started_at ||
      dayHasElapsed,
  )
  const classes = useStyles()
  const isInbound = integration.type === 'INPUT'
  const DirectionIcon = isInbound ? CloudDownloadIcon : CloudUploadIcon

  const titleRef = useRef(null)
  const sectionRef = useRef(null)

  const isAdmin = useAppSelector(selectIsUserAdmin)
  const history = useHistory()
  const { id } = useParams()

  const showTooltip = useHasEllipsis({
    parentElRef: sectionRef,
    childElRef: titleRef,
    offset: 40,
  })

  const editIntegration = () => {
    history.push(`/admin/templates/edit-template/${id}/connect`)
  }

  const runIntegrationAssignment = useCallback(async () => {
    setCanRunNow(false)
    await api
      .post(`/api/v1/integration-assignments/${integrationAssignment.id}/trigger-manual-sync`)
      .catch()
  }, [integrationAssignment])

  const integrationName = integrationAssignment.name || integration.name || t`Unnamed`

  return (
    <Card className={classes.root} key={integration.id} elevation={3}>
      <CardHeaderContainer style={{ background: BACKGROUND_COLORS[integration.slug] }}>
        <CardHeaderImage
          src={`https://claravine-public-prod.s3-us-west-2.amazonaws.com/images/${integration.slug}.png`}
        />
      </CardHeaderContainer>
      <CardContent ref={sectionRef}>
        <Tooltip placement="top" title={showTooltip ? integrationName : ''} arrow>
          <Typography gutterBottom variant="h3" className={classes.name}>
            <span ref={titleRef}>{integrationName}</span>
          </Typography>
        </Tooltip>
        <Typography gutterBottom variant="body2" component={ClampedText3Lines}>
          {integrationAssignment.long_description || integration.description}
        </Typography>
        <PropertyContainer>
          <FlexRow>
            <CheckCircleIcon className={classes.activeIcon} />
            <Typography variant="body2">{t`Active`}</Typography>
          </FlexRow>
          <FlexRow>
            <DirectionIcon className={classes.directionIcon} />
            <Typography variant="body2">{isInbound ? t`Pulls data` : t`Pushes data`}</Typography>
          </FlexRow>
        </PropertyContainer>
      </CardContent>
      <CardActions>
        {isInbound && SUPPORTS_RUN_NOW.includes(integration.slug) && (
          <Tooltip
            placement="top-start"
            title={!canRunNow ? t`Integration sync already running. Please try again later.` : ''}
            enterDelay={150}
            classes={{ tooltip: classes.customWidth }}
          >
            <div className={classes.buttonWrapper}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth={true}
                startIcon={<PlayCircleOutlineIcon />}
                onClick={runIntegrationAssignment}
                disabled={!canRunNow}
              >
                {t`Run now`}
              </Button>
            </div>
          </Tooltip>
        )}
        {isAdmin && (
          <Button
            variant="outlined"
            color="primary"
            fullWidth={true}
            onClick={() => {
              editIntegration()
            }}
            className={classes.editIntegration}
          >
            {t`Edit integration`}
          </Button>
        )}
      </CardActions>
    </Card>
  )
}

const BACKGROUND_COLORS = {
  'email-output': 'linear-gradient(63deg, rgba(255,255,255,.6) 0%, rgba(255,177,177,.6) 100%)',
  'adobe-experience-manager-inbound':
    'linear-gradient(45deg, rgba(250,161,0,.6) 0%, rgba(250,98,0,.6) 100%)',
  'adobe-experience-manager-outbound':
    'linear-gradient(45deg, rgba(250,98,0,.6) 0%, rgba(250,161,0,.6) 100%)',
  'box-push-general': 'linear-gradient(153deg, rgba(255,255,255,.6) 0%, rgba(20,129,196,.25) 100%)',
}

const PropertyContainer = styled.div`
  margin-top: 8px;
`

const ClampedText3Lines = styled.div`
  height: 48px;
  overflow: hidden;
  position: relative;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  text-overflow: -o-ellipsis-lastline;
  -webkit-line-clamp: 4;
`

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2px;
  & > * + * {
    margin-left: 8px;
  }
`

const CardHeaderContainer = styled.div`
  padding: 24px;
  text-align: center;
  position: relative;
`

const CardHeaderImage = styled.img`
  height: 64px;
`
